import React, { useState, useEffect } from "react";
import {
	TextField,
	Button,
	Typography,
	Paper,
	Container,
	Grid,
	Box,
	Chip,
	LinearProgress,
	Card,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	CardContent,
	CardMedia,
	Divider,
	Alert,
	Snackbar,
} from "@mui/material";
import {
	Description,
	MovieCreation,
	Send,
	AutoAwesome,
	VideoCall,
	CheckCircle,
} from "@mui/icons-material";
import workflow from "../images/workflow.gif";

const VideoScriptPage = () => {
	const [description, setDescription] = useState("Topic");
	const [generatedScript, setGeneratedScript] = useState("");
	const [isScriptGenerated, setIsScriptGenerated] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [quota, setQuota] = useState(0);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: "",
		severity: "success",
	});

	useEffect(() => {
		getUserInfo();
	}, []);

	const handleDescriptionChange = (e) => {
		setDescription(e.target.value);
	};

	const handleGenerateScript = async () => {
		if (!description || description === "Topic") {
			setSnackbar({
				open: true,
				message: "Please enter a valid topic",
				severity: "error",
			});
			return;
		}

		setIsLoading(true);

		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url =
				"https://pmco1162s7.execute-api.us-east-1.amazonaws.com/prod";

			const body = {
				task: "getScriptFromTopic",
				topic: description,
				prompt: "",
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();
			setGeneratedScript(data.script);
			setIsScriptGenerated(true);
			setSnackbar({
				open: true,
				message: "Script generated successfully",
				severity: "success",
			});
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to generate script",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const getUserInfo = async () => {
		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url =
				"https://pmco1162s7.execute-api.us-east-1.amazonaws.com/prod";

			const body = {
				task: "getUserInfo",
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const user_info = await response.json();
			setQuota(user_info[0].quota);
			localStorage.setItem("user_info", JSON.stringify(user_info[0]));
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to fetch user information",
				severity: "error",
			});
		}
	};

	const handleSubmitScript = async () => {
		const num_words = generatedScript.split(" ").length;
		const topic_char_count = description.length;

		if (
			description === "" ||
			topic_char_count > 20 ||
			generatedScript === "" ||
			num_words < 30 ||
			num_words > 170
		) {
			setSnackbar({
				open: true,
				message:
					"Please enter a valid topic and script. Topic should be between 1 and 20 characters. Script should be between 30 and 170 words.",
				severity: "error",
			});
			return;
		}

		setIsLoading(true);

		try {
			const auth_user = await localStorage.getItem("auth_user");
			const id_token = JSON.parse(auth_user)?.id_token;

			const headers = {
				Authorization: `Bearer ${id_token}`,
				"Content-Type": "application/json",
			};

			const base_url =
				"https://pmco1162s7.execute-api.us-east-1.amazonaws.com/prod";

			const body = {
				task: "submitVideoGenRequest",
				script: generatedScript,
				topic: description,
			};

			const response = await fetch(base_url, {
				method: "POST",
				headers: headers,
				body: JSON.stringify(body),
			});

			if (!response.ok) {
				throw new Error(`HTTP error! Status: ${response.status}`);
			}

			const data = await response.json();

			if (data.code === 200) {
				setSnackbar({
					open: true,
					message:
						"Your script has been submitted. You'll receive an email (usually 5-10 minutes) when the video is ready.",
					severity: "success",
				});
			} else {
				setSnackbar({
					open: true,
					message: "Something went wrong. Please try again later.",
					severity: "error",
				});
			}
		} catch (err) {
			console.log(err.message);
			setSnackbar({
				open: true,
				message: "Failed to submit script",
				severity: "error",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleScriptChange = (e) => {
		setGeneratedScript(e.target.value);
	};

	const handleCloseSnackbar = () => {
		setSnackbar({ ...snackbar, open: false });
	};

	// Calculate word count for display
	const wordCount = generatedScript ? generatedScript.split(" ").length : 0;
	const isWordCountValid = wordCount >= 30 && wordCount <= 170;

	return (
		<Container maxWidth="lg" className="py-8">
			<Paper elevation={3} className="rounded-lg overflow-hidden">
				<Box className="bg-gradient-to-r from-blue-500 to-purple-600 p-6 text-white">
					<Typography variant="h4" className="font-bold text-center">
						Create Video Script
					</Typography>
					<Typography
						variant="subtitle1"
						className="text-center mt-2"
					>
						Generate and submit scripts for AI video creation
					</Typography>
				</Box>

				<CardContent className="p-6 bg-white">
					<Box className="mb-6">
						<Grid container spacing={2} className="mb-4">
							<Grid item xs={12} md={6}>
								<Chip
									icon={<AutoAwesome />}
									label={`Available Credits: ${quota}/20`}
									color="primary"
									variant="outlined"
									className="px-4 py-3"
								/>
							</Grid>
						</Grid>

						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center"
						>
							<Description className="mr-2" />
							Topic or Video Description
						</Typography>

						<TextField
							fullWidth
							variant="outlined"
							placeholder="Enter a topic or description..."
							value={description}
							onChange={handleDescriptionChange}
							helperText="Keep your topic under 20 characters"
							className="mb-4"
						/>

						<Button
							variant="contained"
							color="primary"
							onClick={handleGenerateScript}
							startIcon={<MovieCreation />}
							className="font-bold py-3 px-8 rounded-full shadow-lg hover:shadow-xl transition-all"
							disabled={isLoading}
						>
							Generate Script
						</Button>
					</Box>

					{isLoading && <LinearProgress className="mb-6" />}

					<Divider className="my-4" />

					<Box className="mt-6">
						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center"
						>
							<Description className="mr-2" />
							{/* Script Editor */}
							Generate or Write Script
						</Typography>

						<TextField
							fullWidth
							multiline
							rows={10}
							variant="outlined"
							value={generatedScript}
							onChange={handleScriptChange}
							placeholder="Your script will appear here..."
							className="mb-4"
						/>

						<Box className="flex justify-between items-center mb-4">
							<Typography
								variant="body2"
								className={`${
									isWordCountValid
										? "text-green-600"
										: "text-red-600"
								}`}
							>
								Word count: {wordCount}/170{" "}
								{!isWordCountValid && "(30-170 words required)"}
							</Typography>

							<Button
								variant="contained"
								color="success"
								onClick={handleSubmitScript}
								startIcon={<Send />}
								disabled={isLoading || !isWordCountValid}
								className="bg-green-600 hover:bg-green-700 font-bold py-3 px-6 rounded-full shadow-lg hover:shadow-xl transition-all"
							>
								Submit Script for Video
							</Button>
						</Box>
					</Box>

					<Divider className="my-4" />

					<Box className="mt-6">
						<Typography
							variant="h6"
							className="font-bold mb-3 flex items-center"
						>
							<VideoCall className="mr-2" />
							Video Creation Process
						</Typography>

						<Box className="mt-4">
							<Grid container spacing={2} alignItems="center">
								<Grid item xs={12} md={6}>
									<List className="pl-4">
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="primary"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Enter a topic and generate a script" />
										</ListItem>
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="primary"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Edit the script as needed (30-170 words)" />
										</ListItem>
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="primary"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Submit your script for video creation" />
										</ListItem>
										<ListItem className="pl-0">
											<ListItemIcon className="min-w-0 mr-2">
												<CheckCircle
													color="primary"
													fontSize="small"
												/>
											</ListItemIcon>
											<ListItemText primary="Receive your video within 5-10 minutes via email" />
										</ListItem>
									</List>
								</Grid>
								<Grid item xs={12} md={6}>
									<CardMedia
										component="img"
										image={workflow}
										alt="Video creation workflow"
										className="w-full h-auto object-contain rounded-lg"
										style={{ maxWidth: "100%" }}
									/>
								</Grid>
							</Grid>
						</Box>
					</Box>
				</CardContent>
			</Paper>

			<Snackbar
				open={snackbar.open}
				autoHideDuration={6000}
				onClose={handleCloseSnackbar}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
			>
				<Alert
					onClose={handleCloseSnackbar}
					severity={snackbar.severity}
					variant="filled"
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</Container>
	);
};

export default VideoScriptPage;
